import { useLayoutEffect, useRef, useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RyeIcon } from "./RyeIcon";
import { RyeTooltip } from "./RyeTooltip";

export type SegmentedButtonEntry = {
  id: string;
  displayText?: string;
  icon?: string;
  onClick: () => void;
  tooltip?: string;
};

type ThumbRect = { left: number; width: number };

export function RyeSegmentedButton({
  entries,
  initEntryId,
  selectedEntryId: controlledEntryId,
}: {
  entries: SegmentedButtonEntry[];
  initEntryId: string;
  // Makes the selection controlled, for when the parent can change it too.
  // Keeps the button mounted, so the thumb slides rather than jumps.
  selectedEntryId?: string;
}) {
  const [uncontrolledEntryId, setSelectedEntryId] = useState(initEntryId);
  const selectedEntryId = controlledEntryId ?? uncontrolledEntryId;
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<ThumbRect | null>(null);
  // The thumb appears in place on first measure, and only slides after that.
  const [canSlide, setCanSlide] = useState(false);

  // Measure the selected entry so the thumb can sit under it. Re-measured on
  // resize, since entry widths shift when the font loads.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    function measure() {
      const entryEl = track?.querySelector<HTMLElement>(
        `[data-segment-id="${CSS.escape(selectedEntryId)}"]`,
      );
      if (!entryEl) {
        return;
      }
      setThumb({ left: entryEl.offsetLeft, width: entryEl.offsetWidth });
    }
    measure();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [selectedEntryId]);

  useLayoutEffect(() => {
    if (thumb && !canSlide) {
      const frame = requestAnimationFrame(() => setCanSlide(true));
      return () => cancelAnimationFrame(frame);
    }
  }, [thumb, canSlide]);

  return (
    <div
      ref={trackRef}
      className={buildClasses(
        "relative",
        "flex",
        "items-center",
        "h-7",
        "p-0.5",
        "rounded-lg",
        "bg-well",
        "text-sm",
      )}
    >
      {thumb && (
        <div
          aria-hidden
          className={buildClasses(
            "absolute",
            "top-0.5",
            "bottom-0.5",
            "left-0",
            "rounded-md",
            "bg-surface-50",
            "shadow-raised",
            {
              if: canSlide,
              then: buildClasses(
                "transition-[transform,width]",
                "duration-200",
                "ease-out",
                "motion-reduce:transition-none",
              ),
            },
          )}
          style={{
            width: thumb.width,
            transform: `translateX(${thumb.left}px)`,
          }}
        />
      )}
      {entries.map((entry) => (
        <Entry
          key={entry.id}
          entry={entry}
          isSelected={selectedEntryId === entry.id}
          setSelectedEntryId={setSelectedEntryId}
        ></Entry>
      ))}
    </div>
  );
}

function Entry(props: {
  entry: SegmentedButtonEntry;
  isSelected: boolean;
  setSelectedEntryId: React.Dispatch<React.SetStateAction<string>>;
}) {
  function handleClick() {
    props.setSelectedEntryId(props.entry.id);
    props.entry.onClick();
  }

  const entryEl = (
    <div
      data-segment-id={props.entry.id}
      className={buildClasses(
        {
          if: props.isSelected,
          then: "text-on-surface-900",
          else: "text-on-surface-500 hover:text-on-surface-700",
        },
        "relative",
        "flex",
        "items-center",
        "h-full",
        "px-3",
        "cursor-pointer",
        "select-none",
        "transition-colors",
        "duration-200",
      )}
      onClick={handleClick}
    >
      <EntryContent entry={props.entry} />
    </div>
  );

  if (props.entry.tooltip) {
    return <RyeTooltip text={props.entry.tooltip}>{entryEl}</RyeTooltip>;
  }

  return entryEl;
}

function EntryContent({ entry }: { entry: SegmentedButtonEntry }): JSX.Element {
  if (entry.icon === undefined && entry.displayText === undefined) {
    return <span></span>;
  }
  if (entry.icon === undefined) {
    return (
      <span className={buildClasses("flex", "items-center", "justify-center")}>
        {entry.displayText}
      </span>
    );
  }
  if (entry.displayText === undefined) {
    return (
      <div className={buildClasses("flex", "items-center", "justify-center")}>
        <RyeIcon name={entry.icon} size="sm" />
      </div>
    );
  }
  return (
    <div
      className={buildClasses(
        "flex",
        "items-center",
        "justify-center",
        "gap-1.5",
      )}
    >
      <RyeIcon name={entry.icon} size="sm" />
      <span>{entry.displayText}</span>
    </div>
  );
}
